import {
  SyntheticEvent,
  UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@touchtech/web-api-client";

import {
  CheckoutConfigurationType,
  CheckoutOptionsType,
  DeliveryOptions,
  LocalStorageKeys,
  PhoneOrEmailInputField,
  PickupInfo,
  ROUTES,
  TranslationContext,
} from "@is/constants";
import { useConfiguration, useSearchRegions, useStoreDevice } from "@is/hooks";
import { useManageBasket } from "@is/hooks/basket";
import { useManageCheckoutConfigurations } from "@is/hooks/checkout";
import { useOrderCheckout } from "@is/hooks/useOrderCheckout";
import { LocalStorageService } from "@is/services";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import {
  Agreement,
  CheckoutAgreementOrderType,
  CheckoutAttempt,
  CheckoutFormState,
  CheckoutSection,
  FieldState,
  Order,
  ReducedFieldState,
  ValidateOrder,
} from "@is/types";
import { CheckoutInputFields, FieldsValidation } from "@is/types/checkout";
import {
  convertCountryCodeToCountryName,
  getLanguageFromLocalStorage,
} from "@is/utils";
import {
  INPUT_EMAIL_REGEX,
  INPUT_ZIP_CODE,
  validateCheckoutField,
} from "@is/utils/regex";
import { getCurrentSessionId, markSessionFinished } from "@is/utils/session";
import { sendErrorMessageToSlack } from "@is/utils/slack";
import { toCamelCase } from "@is/utils/string";

type Props = {
  onClose: () => void;
  onEditQuantity: () => void;
  checkoutConfigurationsType: CheckoutConfigurationType;
  isOpen: boolean;
};

// A text field holding only whitespace carries no value, so a required one counts
// as empty. Typed fields (email, phone, phoneOrEmail) keep comparing against the
// empty string: they already reject whitespace through their own format check or
// server-side validation, and treating it as "empty" here would replace those
// errors with a generic required-field one.
const isBlankFieldValue = (value: string, type?: string) =>
  type === "text" ? value.trim() === "" : value === "";

const useManageCheckoutDrawerContent = ({
  onClose,
  onEditQuantity,
  checkoutConfigurationsType,
}: Props) => {
  const { t } = useTranslation(TranslationContext.Checkout);

  const containerRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const [{ isPending, error: errorOrderCheckout }, orderCheckout] =
    useOrderCheckout();

  const [formState, setFormState] = useState<CheckoutFormState>({});
  const [editingMode, setEditingMode] = useState(true);
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showExternalPage, setShowExternalPage] = useState(false);
  const [showFieldsErrorMessage, setShowFieldsErrorMessage] = useState(false);
  const [showInvalidFields, setShowInvalidFields] = useState(false);
  const [showPaymentErrorMessage, setShowPaymentErrorMessage] = useState(false);
  const [externalPageSrc, setExternalPageSrc] = useState("");
  const [checkoutAgreements, setCheckoutAgreements] = useState<Agreement[]>([]);
  const [phoneOrEmail, setPhoneOrEmail] = useState(
    PhoneOrEmailInputField.Phone
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [activeDeliveryOption, setActiveDeliveryOption] =
    useState<DeliveryOptions>(DeliveryOptions.HomeDelivery);
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());
  const continueRequestedRef = useRef(false);
  const inFlightValidationRef = useRef(null);

  const locale = getLanguageFromLocalStorage();
  const countryCode = LocalStorageService.getItem(LocalStorageKeys.CountryCode);

  const { regions } = useSearchRegions({
    countryCode,
    zipCode: formState?.zipCode?.value || "",
  });

  const { checkoutAgreements: configuredCheckoutAgreements } =
    useConfiguration();

  const { totalItems, basketItems, totalPrice } = useManageBasket();

  const {
    checkoutConfigurations,
    validationError,
    validateCheckoutForm,
    isPending: isValidationPending,
  } = useManageCheckoutConfigurations();

  const { data: storeInfo } = useStoreDevice();

  const storeName = useMemo(() => storeInfo?.name || "", [storeInfo]);

  const storeAddress = useMemo(() => {
    const address = storeInfo?.location?.address || "";
    const zipCode = storeInfo?.location?.zipCode || "";
    const city = storeInfo?.location?.city || "";

    const fullAddress = [address, zipCode, city].filter(Boolean);

    return fullAddress.join(", ");
  }, [storeInfo]);

  const checkoutConfiguration = useMemo(
    () =>
      checkoutConfigurations?.find(
        (configuration) => configuration?.type === checkoutConfigurationsType
      ),
    [checkoutConfigurationsType, checkoutConfigurations]
  );

  const getValidationRegex = useCallback(
    (name: string) => {
      const isSelectedEmailFromPhoneOrEmail =
        phoneOrEmail === PhoneOrEmailInputField.Email &&
        name === CheckoutInputFields.PhoneNumberOrEmail;

      if (
        name === CheckoutInputFields.Email ||
        isSelectedEmailFromPhoneOrEmail
      ) {
        return INPUT_EMAIL_REGEX;
      }

      if (name === CheckoutInputFields.ZipCode) {
        return INPUT_ZIP_CODE;
      }

      return null;
    },
    [phoneOrEmail]
  );

  const convertConfigurationsToFormState = useCallback(
    (options: CheckoutSection, parentType: CheckoutOptionsType) => {
      const storedCheckoutInfo = JSON.parse(
        LocalStorageService.getItem(LocalStorageKeys.CheckoutInfo)
      );

      return Object.values(options)?.reduce((acc, currentValue) => {
        if (currentValue?.properties && currentValue.isEnabled) {
          currentValue.properties.forEach((property) => {
            if (property.isEnabled) {
              acc = {
                ...acc,
                [toCamelCase(property.name)]: {
                  ...property,
                  value:
                    storedCheckoutInfo &&
                    storedCheckoutInfo[checkoutConfigurationsType][
                      toCamelCase(property?.name)
                    ]
                      ? storedCheckoutInfo[checkoutConfigurationsType][
                          toCamelCase(property?.name)
                        ]?.value
                      : "",
                  name: toCamelCase(property.name),
                  parentType,
                  ...(!!getValidationRegex(toCamelCase(property.name)) && {
                    validationRegex: getValidationRegex(
                      toCamelCase(property.name)
                    ),
                  }),
                  ...(toCamelCase(property.name) ===
                    CheckoutInputFields.Country && { isDisabled: true }),
                },
              };
            }
          });
        }

        return acc;
      }, {});
    },
    [checkoutConfigurationsType, getValidationRegex]
  );

  const homeDeliveryOption: { [key: string]: FieldState } = useMemo(() => {
    const options = checkoutConfiguration?.deliveryOptions || {};
    const filteredOptions: CheckoutSection = Object.entries(options)?.reduce(
      (acc, [key, value]) => {
        if (key === DeliveryOptions.HomeDelivery) {
          acc[key] = value;
        }

        return acc;
      },
      {}
    );

    return convertConfigurationsToFormState(
      filteredOptions,
      CheckoutOptionsType.DeliveryOptions
    );
  }, [checkoutConfiguration, convertConfigurationsToFormState]);

  const recipientOptions: { [key: string]: FieldState } = useMemo(
    () =>
      checkoutConfiguration?.recipientOptions
        ? convertConfigurationsToFormState(
            checkoutConfiguration?.recipientOptions,
            CheckoutOptionsType.RecipientOptions
          )
        : {},
    [checkoutConfiguration, convertConfigurationsToFormState]
  );

  const instorePickupOptions = useMemo(() => {
    const pickupStoreInfo = {
      [PickupInfo.PickupStoreAddress]: storeInfo?.location?.address || "",
      [PickupInfo.PickupStoreId]: storeInfo?.storeId || "",
      [PickupInfo.PickupStoreZipCode]: storeInfo?.location?.zipCode || "",
      [PickupInfo.PickupStoreCity]: storeInfo?.location?.city || "",
      [PickupInfo.PickupStoreCountryCode]:
        storeInfo?.location?.countryCode || "",
    };

    return checkoutConfiguration?.deliveryOptions?.[
      DeliveryOptions.InstorePickup
    ]?.properties?.map((property) => ({
      ...property,
      value: pickupStoreInfo[property.id],
    }));
  }, [checkoutConfiguration, storeInfo]);

  const areHomeDeliveryAndInstorePickupOptionsEnabled = useMemo(() => {
    const options = checkoutConfiguration?.deliveryOptions || {};
    const filteredOptions = Object.values(options).filter(
      (option) => option.isEnabled
    );

    return filteredOptions.length > 1;
  }, [checkoutConfiguration]);

  const isOnlyInstorePickupOptionEnabled = useMemo(() => {
    const options = checkoutConfiguration?.deliveryOptions || {};
    const enabledOptions = Object.entries(options).reduce(
      (acc, [key, value]) => {
        if (value.isEnabled) {
          acc = { ...acc, [key]: value };
        }

        return acc;
      },
      {}
    );

    return (
      Object.keys(enabledOptions).length === 1 &&
      Object.keys(enabledOptions)[0] === DeliveryOptions.InstorePickup
    );
  }, [checkoutConfiguration]);

  const isInstorePickupOptionSelected = useMemo(
    () => activeDeliveryOption === DeliveryOptions.InstorePickup,
    [activeDeliveryOption]
  );

  const formStateDeliveryOptions = useMemo(
    () =>
      Object.values(formState).filter(
        (field) => field.parentType === CheckoutOptionsType.DeliveryOptions
      ),
    [formState]
  );

  const formStateRecipientOptions = useMemo(
    () =>
      Object.values(formState).filter(
        (field) => field.parentType === CheckoutOptionsType.RecipientOptions
      ),
    [formState]
  );

  const requiredFieldsAreFilled = useMemo(() => {
    const fields =
      activeDeliveryOption === DeliveryOptions.InstorePickup
        ? formStateRecipientOptions
        : Object.values(formState);

    return fields.every((field) =>
      field.isRequired ? !isBlankFieldValue(field.value, field.type) : true
    );
  }, [activeDeliveryOption, formState, formStateRecipientOptions]);

  const handleScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    const { scrollTop } = event.currentTarget;
    setIsScrolled(scrollTop > 0);
  }, []);

  const areMandatoryFieldsEmpty = useMemo(() => {
    const fields =
      activeDeliveryOption === DeliveryOptions.InstorePickup
        ? formStateRecipientOptions
        : Object.values(formState);

    return fields.some((fieldState) => {
      if (fieldState.isRequired) {
        return isBlankFieldValue(fieldState.value, fieldState.type);
      }

      return false;
    });
  }, [activeDeliveryOption, formState, formStateRecipientOptions]);

  const fieldsValidation = useMemo(() => {
    const state =
      activeDeliveryOption === DeliveryOptions.InstorePickup
        ? Object.fromEntries(
            Object.entries(formState).filter(
              ([, value]) =>
                value.parentType !== CheckoutOptionsType.DeliveryOptions
            )
          )
        : formState;

    return Object.entries(state).reduce((acc: FieldsValidation, fieldState) => {
      const [field, { value, validationRegex, maxLength, isRequired, type }] =
        fieldState;

      let notValid = false;
      let errorMessage = "";

      // Show format errors when user provided enough signal (not on first char)
      // Trigger on: contains '@' OR length >= 5 OR after first Continue
      const shouldCheckFormat =
        showInvalidFields ||
        (value && (value.includes("@") || value.length >= 5));

      if (!notValid && shouldCheckFormat && value && validationRegex) {
        const regex = new RegExp(validationRegex);
        notValid = !regex.test(value);
      }

      // Length check always applies
      if (!notValid && maxLength) {
        notValid = value.length > maxLength;
        if (notValid) {
          errorMessage = t(`Maximum {{maxLength}} symbols`, {
            maxLength,
          });
        }
      }

      // Only show required empty after first Continue click
      if (!notValid && showInvalidFields && isRequired) {
        notValid = isBlankFieldValue(value, type);
      }

      // Merge backend errors only if field hasn't been edited since last validation
      if (!notValid && validationError?.body) {
        validationError?.body.forEach((item) => {
          if (toCamelCase(item.name) === field) {
            if (!dirtyFields.has(field)) {
              notValid = true;
              errorMessage = t(item.message);
            }
          }
        });
      }

      return {
        ...acc,
        [field]: {
          error: notValid,
          message: errorMessage,
        },
      };
    }, {});
  }, [
    activeDeliveryOption,
    formState,
    showInvalidFields,
    validationError,
    t,
    dirtyFields,
  ]);

  const getValidatableState = useCallback(() => {
    return activeDeliveryOption === DeliveryOptions.InstorePickup
      ? Object.fromEntries(
          Object.entries(formState).filter(
            ([, value]) =>
              value.parentType !== CheckoutOptionsType.DeliveryOptions
          )
        )
      : formState;
  }, [activeDeliveryOption, formState]);

  const getInvalidFields = useCallback(() => {
    const state = getValidatableState();
    return Object.entries(state)
      .filter(([, { value, validationRegex, maxLength }]) => {
        if (maxLength && value.length > maxLength) {
          return true;
        }
        if (validationRegex && value) {
          const regex = new RegExp(validationRegex);
          return !regex.test(value);
        }
        return false;
      })
      .map(([field]) => field);
  }, [getValidatableState]);

  // Raw input format errors independent of UI error display state
  const hasInputFormatErrors = useMemo(
    () => getInvalidFields().length > 0,
    [getInvalidFields]
  );

  const hasBackendErrors = useMemo(
    () =>
      !!validationError?.body?.some(
        (item) => !dirtyFields.has(toCamelCase(item.name))
      ),
    [validationError, dirtyFields]
  );

  const passedAllValidations = useMemo(
    () => !hasInputFormatErrors && requiredFieldsAreFilled && !hasBackendErrors,
    [hasInputFormatErrors, requiredFieldsAreFilled, hasBackendErrors]
  );

  const hasChanges = useMemo(() => {
    const initialState = { ...recipientOptions, ...homeDeliveryOption };

    return Object.entries(formState).some(([field, { value }]) => {
      if (field === CheckoutInputFields.Country) {
        return false;
      }

      return value !== initialState[field]?.value;
    });
  }, [homeDeliveryOption, formState, recipientOptions]);

  const hasOptions = useMemo(
    () =>
      !!Object.keys(homeDeliveryOption).length ||
      !!Object.keys(recipientOptions).length,
    [homeDeliveryOption, recipientOptions]
  );

  const handleFormFieldChange = useCallback(
    (name: CheckoutInputFields, type: string, value: string) => {
      const validatedValue = validateCheckoutField(
        name as CheckoutInputFields,
        type,
        value
      );

      setFormState((prevState) => ({
        ...prevState,
        [name]: {
          ...prevState[name],
          value: validatedValue,
        },
      }));

      setDirtyFields((prev) => {
        const next = new Set(prev);
        next.add(name);
        return next;
      });
    },
    []
  );

  const getOrderProperties = useCallback(() => {
    let properties;

    if (activeDeliveryOption === DeliveryOptions.InstorePickup) {
      const recipientOptions = Object.values(formState).reduce(
        (acc, fieldState) => {
          if (fieldState.parentType === CheckoutOptionsType.RecipientOptions) {
            return { ...acc, [fieldState.name]: fieldState };
          }

          return acc;
        },
        {}
      );

      properties = [
        ...Object.values(recipientOptions),
        ...instorePickupOptions,
      ].map((property: ReducedFieldState) => ({
        id: property.id ? property.id : property.name,
        value: property.value,
      }));
    } else {
      properties = Object.values(formState).map((fieldState) => ({
        id: fieldState.id,
        value:
          fieldState.name === CheckoutInputFields.Country
            ? LocalStorageService.getItem(LocalStorageKeys.CountryCode)
            : fieldState.value,
      }));
    }

    return properties;
  }, [activeDeliveryOption, formState, instorePickupOptions]);

  const handleValidateCheckoutForm = useCallback(async () => {
    const order: ValidateOrder = {
      id: checkoutConfiguration?.id,
      properties: getOrderProperties(),
      validateCountryWithZipCode: false,
    };

    if (inFlightValidationRef.current) {
      const result = await inFlightValidationRef.current;
      setDirtyFields(new Set());
      return result;
    }

    const promise = validateCheckoutForm(order).finally(() => {
      inFlightValidationRef.current = null;
    });
    inFlightValidationRef.current = promise;
    const result = await promise;
    setDirtyFields(new Set());
    return result;
  }, [checkoutConfiguration, getOrderProperties, validateCheckoutForm]);

  const handleSaveCheckoutInfo = useCallback(async () => {
    const data = {
      [checkoutConfigurationsType]: formState,
    };

    LocalStorageService.setItem(LocalStorageKeys.CheckoutInfo, data);

    const hasLocalInvalids = getInvalidFields().length > 0;

    if (
      !areMandatoryFieldsEmpty &&
      !hasLocalInvalids &&
      !continueRequestedRef.current
    ) {
      await handleValidateCheckoutForm();
    }
  }, [
    areMandatoryFieldsEmpty,
    checkoutConfigurationsType,
    formState,
    handleValidateCheckoutForm,
    getInvalidFields,
  ]);

  const agreementsHasTranslation = useMemo(
    () =>
      checkoutAgreements.filter(
        (agreement) =>
          !!Object.keys(agreement?.translations)?.includes(locale) ||
          !!Object.keys(agreement?.translations)?.includes(
            agreement?.fallbackLanguage?.code
          )
      ),
    [checkoutAgreements, locale]
  );

  const isConfirmDisabled = useMemo(() => {
    const filteredRequiredFields = agreementsHasTranslation.filter(
      (agreement) => agreement.isRequired
    );
    const allRequiredFieldsChecked = filteredRequiredFields.every(
      (agreement) => agreement.isChecked
    );

    return !allRequiredFieldsChecked || isPending;
  }, [agreementsHasTranslation, isPending]);

  const handleCompleteOrder = useCallback(async () => {
    try {
      const notValidFields = getInvalidFields();

      setShowInvalidFields(true);
      setShowFieldsErrorMessage(true);

      const areFieldsNotValid = notValidFields.length > 0;

      if (areMandatoryFieldsEmpty || areFieldsNotValid) {
        trackEvent({
          eventType: TrackingEventTypes.CheckoutInputValidationError,
          data: { errorFields: notValidFields },
        });
        return;
      }

      const validation = await handleValidateCheckoutForm();

      if (!validation) {
        return;
      }

      const filledFields = Object.entries(formState)
        .filter(([, { value }]) => value !== "")
        .map(([field]) => field);

      trackEvent({
        eventType: TrackingEventTypes.CheckoutConfirm,
        data: { filledFields },
      });

      trackEvent({
        eventType: TrackingEventTypes.SummaryOpen,
        data: {},
      });

      hasChanges &&
        trackEvent({
          eventType: TrackingEventTypes.CheckoutFormUsed,
          data: {},
        });

      setEditingMode(false);
    } finally {
      continueRequestedRef.current = false;
    }
  }, [
    getInvalidFields,
    formState,
    areMandatoryFieldsEmpty,
    handleValidateCheckoutForm,
    hasChanges,
  ]);

  const handleContinueMouseDown = useCallback(() => {
    continueRequestedRef.current = true;
  }, []);

  const handleContinueMouseUp = useCallback(() => {
    continueRequestedRef.current = false;
  }, []);

  const handleContinueMouseLeave = useCallback(() => {
    continueRequestedRef.current = false;
  }, []);

  const handleContinueTouchEnd = useCallback(() => {
    continueRequestedRef.current = false;
  }, []);

  const handleContinueTouchCancel = useCallback(() => {
    continueRequestedRef.current = false;
  }, []);

  const handleConfirmOrder = useCallback(async () => {
    if (isConfirmDisabled) {
      setShowPaymentErrorMessage(true);
      return;
    }

    trackEvent({
      eventType: TrackingEventTypes.SummaryConfirm,
      data: {},
    });

    setShowPaymentErrorMessage(false);

    const preparedAgreements: CheckoutAgreementOrderType[] =
      checkoutAgreements.map((agreement) => ({
        isChecked: agreement.isChecked,
        isRequired: agreement.isRequired,
        title: agreement.title,
      }));

    const order: Order = {
      checkoutConfigurationId: checkoutConfiguration?.id,
      properties: getOrderProperties(),
      validateCountryWithZipCode: false,
      checkoutAgreements: preparedAgreements,
    };

    // Captured BEFORE the request: an inactivity or manual reset can land while
    // it is in flight, and the record must name the session that actually
    // checked out, never the live one a reset just created (ticket aecd58ce).
    const checkoutSessionId = getCurrentSessionId();

    const result = await orderCheckout(order);

    if (!result) {
      setIsFailedModalOpen(true);

      trackEvent({
        eventType: TrackingEventTypes.SummaryConfirmFailed,
        data: {},
      });
    } else {
      // The order landed, so the backend has locked this basket: record the
      // session as finished NOW rather than leaving it to the confirmation
      // screen's ~10s timer, which never fires if the tablet sleeps or the page
      // reloads first. Written before the navigation below so an interruption
      // cannot beat it.
      markSessionFinished(checkoutSessionId);

      setEditingMode(true);
      setCheckoutAgreements((prevState) =>
        prevState.map((agreement) => ({
          ...agreement,
          isChecked: false,
        }))
      );

      trackEvent({
        eventType: TrackingEventTypes.SummaryConfirmSuccess,
        data: {
          totalItems,
          totalPrice,
          countryCode: basketItems[0]?.price?.countryCode,
          currencyCode: basketItems[0]?.price?.currencyCode,
          basketItems: basketItems.map((item) => ({
            ean: item.ean,
            styleId: item.productId,
            variantId: item.variantId,
          })),
        },
      });

      navigate(ROUTES.ConfirmedOrderView);

      onClose();
    }
  }, [
    isConfirmDisabled,
    checkoutAgreements,
    checkoutConfiguration,
    getOrderProperties,
    orderCheckout,
    totalItems,
    totalPrice,
    basketItems,
    navigate,
    onClose,
  ]);

  const handleToggleCheckbox = useCallback((id: string) => {
    setCheckoutAgreements((prevState) =>
      prevState.map((agreement) =>
        agreement.id == id
          ? { ...agreement, isChecked: !agreement.isChecked }
          : agreement
      )
    );
  }, []);

  const handleResetErrors = useCallback(() => {
    setShowFieldsErrorMessage(false);
    setShowInvalidFields(false);
  }, []);

  const handleEdit = useCallback(() => {
    if (editingMode) {
      onEditQuantity();

      hasChanges &&
        trackEvent({
          eventType: TrackingEventTypes.CheckoutFormUsed,
          data: {},
        });

      handleResetErrors();
    } else {
      setEditingMode(true);

      trackEvent({
        eventType: TrackingEventTypes.SummaryBackToEdit,
        data: {},
      });
    }
  }, [editingMode, handleResetErrors, hasChanges, onEditQuantity]);

  const handleCloseExternalPage = useCallback(() => {
    setShowExternalPage(false);
    setExternalPageSrc("");
  }, []);

  const handleOpenExternalPage = useCallback((src: string) => {
    setShowExternalPage(true);
    setExternalPageSrc(src);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    handleResetErrors();
    onClose();

    hasChanges &&
      trackEvent({
        eventType: TrackingEventTypes.CheckoutFormUsed,
        data: {},
      });
  }, [handleResetErrors, hasChanges, onClose]);

  const handlePhoneOrEmailChange = useCallback(
    (name: PhoneOrEmailInputField) => {
      setPhoneOrEmail(name);
      const storedCheckoutInfo = JSON.parse(
        LocalStorageService.getItem(LocalStorageKeys.CheckoutInfo)
      );

      if (storedCheckoutInfo) {
        const newStoredCheckoutInfo = {
          [checkoutConfigurationsType]: {
            ...storedCheckoutInfo[checkoutConfigurationsType],
            [CheckoutInputFields.PhoneNumberOrEmail]: {
              ...storedCheckoutInfo[checkoutConfigurationsType][
                CheckoutInputFields.PhoneNumberOrEmail
              ],
              value: "",
            },
          },
        };

        LocalStorageService.setItem(
          LocalStorageKeys.CheckoutInfo,
          newStoredCheckoutInfo
        );
      }
    },
    [checkoutConfigurationsType]
  );

  const handleToggleDeliveryOptions = useCallback((event: SyntheticEvent) => {
    const id = event.currentTarget.getAttribute("id");

    setActiveDeliveryOption(id as DeliveryOptions);
  }, []);

  useEffect(() => {
    if (showFieldsErrorMessage && !areMandatoryFieldsEmpty) {
      setShowFieldsErrorMessage(false);
    }
  }, [showFieldsErrorMessage, areMandatoryFieldsEmpty]);

  useEffect(() => {
    if (hasOptions) {
      setFormState(() => ({
        ...recipientOptions,
        ...homeDeliveryOption,
      }));
    }
  }, [homeDeliveryOption, hasOptions, recipientOptions]);

  useEffect(() => {
    if (
      countryCode &&
      (homeDeliveryOption?.country || recipientOptions?.country)
    ) {
      setFormState((prevState) => ({
        ...prevState,
        country: {
          ...prevState.country,
          value: convertCountryCodeToCountryName(countryCode),
        },
      }));
    }
  }, [
    countryCode,
    homeDeliveryOption?.country,
    hasOptions,
    recipientOptions?.country,
  ]);

  useEffect(() => {
    if (regions?.length) {
      const { region: countryRegion, city } = regions[0];

      setFormState((prevState) => ({
        ...prevState,
        ...(prevState?.stateRegion && {
          stateRegion: { ...prevState.stateRegion, value: countryRegion },
        }),
        ...(prevState?.city && { city: { ...prevState.city, value: city } }),
      }));
    }
  }, [regions]);

  useEffect(() => {
    if (configuredCheckoutAgreements && countryCode) {
      const filteredAgreements = configuredCheckoutAgreements.filter(
        (agreement) =>
          !agreement.countries ||
          agreement.countries.length === 0 ||
          agreement.countries
            .map((country) => country.toUpperCase())
            .includes(countryCode.toUpperCase())
      );

      const newAgreements = filteredAgreements.map((agreement) => ({
        ...agreement,
        isChecked: false,
      }));

      setCheckoutAgreements(newAgreements);
    }
  }, [countryCode, configuredCheckoutAgreements]);

  useEffect(() => {
    if (errorOrderCheckout) {
      const error = (errorOrderCheckout as ApiError).body as object & {
        checkoutAttempts?: CheckoutAttempt[];
      };
      const filteredError = error
        ? {
            ...error,
            checkoutAttempts: error["checkoutAttempts"]?.map(
              (checkoutAttempt) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { payload, ...restCheckoutAttemptProps } =
                  checkoutAttempt;
                return restCheckoutAttemptProps;
              }
            ),
          }
        : {};

      if (error?.checkoutAttempts) {
        const lastCheckoutAttempt = error?.checkoutAttempts?.at(-1);
        const lastCheckoutAttemptsStatusCode =
          lastCheckoutAttempt?.result?.statusCode || 0;
        const lastCheckoutAttemptsErrorMessage =
          lastCheckoutAttempt?.result?.message;

        if (
          lastCheckoutAttemptsStatusCode >= 400 &&
          lastCheckoutAttemptsStatusCode < 500
        ) {
          setErrorMessage(lastCheckoutAttemptsErrorMessage || "");
        }
      }

      const endpointFromError = (errorOrderCheckout as any)?.__endpoint;

      const message = JSON.stringify({
        ...(endpointFromError && { endpoint: endpointFromError }),
        error: filteredError,
      });

      sendErrorMessageToSlack(message);
    }
  }, [errorOrderCheckout]);

  useEffect(() => {
    if (isOnlyInstorePickupOptionEnabled) {
      setActiveDeliveryOption(DeliveryOptions.InstorePickup);
    }
  }, [checkoutConfiguration, isOnlyInstorePickupOptionEnabled]);

  useEffect(() => {
    containerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [editingMode]);

  return {
    containerRef,
    editingMode,
    isScrolled,
    showExternalPage,
    showFieldsErrorMessage,
    showPaymentErrorMessage,
    fieldsValidation,
    handleFormFieldChange,
    handleCompleteOrder,
    handleContinueMouseDown,
    handleContinueMouseUp,
    handleContinueMouseLeave,
    handleContinueTouchEnd,
    handleContinueTouchCancel,
    handleConfirmOrder,
    handleScroll,
    handleCloseExternalPage,
    handleOpenExternalPage,
    isFailedModalOpen,
    setIsFailedModalOpen,
    isConfirmDisabled,
    isCheckoutPending: isPending,
    t,
    externalPageSrc,
    checkoutAgreements,
    handleToggleCheckbox,
    handleCloseDrawer,
    handleEdit,
    formStateRecipientOptions,
    formStateDeliveryOptions,
    requiredFieldsAreFilled,
    phoneOrEmail,
    handlePhoneOrEmailChange,
    handleSaveCheckoutInfo,
    checkoutConfigurationLabel: checkoutConfiguration?.label,
    passedAllValidations,
    isValidationPending,
    errorMessage,
    setErrorMessage,
    handleToggleDeliveryOptions,
    activeDeliveryOption,
    storeName,
    storeAddress,
    areHomeDeliveryAndInstorePickupOptionsEnabled,
    isInstorePickupOptionSelected,
  };
};

export default useManageCheckoutDrawerContent;
